<script lang="ts">
/** Unsaved notes per chat: kept while another chat (in reply mode) is open, which takes this composer away. */
const drafts: Record<string, string> = {}
</script>

<script setup lang="ts">
/**
 * "โน้ตภายใน" mode of the composer area: what is typed here is saved as a staff-only note (its own API and
 * table) — it can never go to the customer. Enter saves, Shift+Enter new line, Esc back to replying.
 */
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useCollab } from '../../stores/collab'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ convId: string }>()
const collab = useCollab()

const text = ref('')
const box = ref<HTMLTextAreaElement | null>(null)

function autosize(): void {
  const el = box.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, 160)}px`
}

watch(
  () => props.convId,
  (id, old) => {
    if (old) drafts[old] = text.value
    text.value = drafts[id] ?? ''
    void nextTick(() => {
      autosize()
      box.value?.focus({ preventScroll: true })
    })
  },
)
onMounted(() => {
  text.value = drafts[props.convId] ?? ''
  void nextTick(() => {
    autosize()
    box.value?.focus({ preventScroll: true })
  })
})
onBeforeUnmount(() => {
  // another chat opened (this one is still in note mode): keep the text · note mode closed (Esc, ยกเลิก): it goes
  if (collab.noteMode[props.convId]) drafts[props.convId] = text.value
  else delete drafts[props.convId]
})

async function save(): Promise<void> {
  if (!text.value.trim() || collab.busy) return
  const id = props.convId
  if (await collab.addNote(id, text.value)) {
    drafts[id] = ''
    if (props.convId !== id) return // another chat opened meanwhile: its text stays
    text.value = ''
    void nextTick(autosize)
  }
}

function onKey(e: KeyboardEvent): void {
  if (e.isComposing) return
  if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
    e.preventDefault()
    void save()
  } else if (e.key === 'Escape') {
    e.preventDefault()
    collab.setNoteMode(props.convId, false)
  }
}
</script>

<template>
  <div class="ncmp-wrap">
    <div class="ncmp">
      <div class="ncmp-h">
        <span class="n-chip"><Icon name="note" />โน้ตภายใน</span>
        <span class="n-sub">เห็นเฉพาะพนักงาน ไม่ส่งถึงลูกค้า</span>
        <span class="grow"></span>
        <span class="kbd-hint"><kbd>Enter</kbd> บันทึก · <kbd>Esc</kbd> กลับไปตอบลูกค้า</span>
      </div>
      <textarea
        ref="box"
        v-model="text"
        class="n-text"
        rows="1"
        aria-label="โน้ตภายใน เห็นเฉพาะพนักงาน"
        placeholder="จดเรื่องที่ทีมควรรู้ เช่น ลูกค้าแพ้กุ้ง สั่งต่อทุกสิ้นเดือน"
        maxlength="1000"
        @input="autosize"
        @keydown="onKey"
      ></textarea>
      <div class="n-tools">
        <span class="n-count" :class="{ warn: text.length > 900 }">{{ text.length }}/1,000</span>
        <span class="grow"></span>
        <button class="n-x" @click="collab.setNoteMode(convId, false)">ยกเลิก</button>
        <button class="n-save" :disabled="!text.trim() || collab.busy" @click="save"><Icon name="check" />บันทึกโน้ต</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ncmp-wrap {
  flex: none;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid var(--line);
}
.ncmp {
  border: 1px solid #ead595;
  border-radius: 12px;
  background: #fffbe6;
}
.ncmp:focus-within {
  border-color: #d6b23c;
}
.ncmp-h {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 0;
  min-width: 0;
}
.n-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px 0 6px;
  border-radius: 6px;
  background: #f6e7ae;
  color: #5e4b0c;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.n-chip :deep(.ic) {
  width: 14px;
  height: 14px;
}
.n-sub,
.kbd-hint {
  font-size: 12px;
  line-height: 18px;
  color: #7a6420;
  white-space: nowrap;
}
.kbd-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
kbd {
  font: 600 12px/18px var(--mono);
  padding: 0 6px;
  border-radius: 5px;
  background: #fff;
  border: 1px solid #e3cf8c;
  border-bottom-width: 2px;
  color: var(--ink);
}
.n-text {
  display: block;
  width: calc(100% - 24px);
  margin: 6px 12px 0;
  padding: 8px 12px;
  border: 0;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  line-height: 22px;
  color: var(--ink);
  outline: none;
  resize: none;
  min-height: 40px;
  max-height: 160px;
  overflow-y: auto;
}
.n-text::placeholder {
  color: #8f7d45;
}
.n-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
}
.n-count {
  padding-left: 4px;
  font: 12px/18px var(--mono);
  color: #8f7d45;
}
.n-count.warn {
  color: var(--crit);
}
.n-x {
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: #5e4b0c;
}
.n-x:hover {
  background: #f6ebbd;
}
.n-save {
  height: 36px;
  padding: 0 14px 0 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
  background: var(--warn);
  color: #fff;
}
.n-save:hover:not(:disabled) {
  background: #6f4100;
}
.n-save:disabled {
  background: #cdb88a;
}
.n-save :deep(.ic) {
  width: 16px;
  height: 16px;
}
</style>
